import { EmptyPlaceholder } from 'config';
import { AccountingExpense } from 'types';
import { formatCalendarDate } from 'utils/date';
import {
  EventPayment,
  EventPaymentTransaction,
  eventPaymentTransactionLabel,
} from 'store/api/event/types';

export type ExpenseDateRow = {
  date?: string | null;
} & Pick<Partial<AccountingExpense>, 'paymentDate'>;

export const getExpenseDate = (expense: ExpenseDateRow): string | null =>
  expense.paymentDate || expense.date || null;

export const formatExpenseDate = (expense: ExpenseDateRow): string => {
  const date = getExpenseDate(expense);

  return date ? formatCalendarDate(date) : EmptyPlaceholder;
};

// The show's location rent, as on the P&L: an application fee counts with it
// (owner 2026-10-16; Main LOCATION_RENT_TRANSACTIONS).
const locationRentTransactions = [
  'deposit',
  'fullPayment',
  'partialPayment',
  'applicationFee',
] as const;

const eventPaymentExpenseCategories: Partial<
  Record<EventPaymentTransaction, string>
> = {
  coi: 'COI',
  electricity: 'Electric Socket',
};

export type EventPaymentExpenseRow = {
  id: string;
  date?: string;
  amount: number;
  notes?: string;
  createdBy?: string;
  receiptUrl?: string;
  categoryName: string;
  employeeName?: string;
  source: 'event-payment';
  paymentMethodName?: string;
};

export const getEventPaymentExpenseRows = (
  payments: EventPayment[] = []
): EventPaymentExpenseRow[] =>
  payments
    .map((payment) => {
      const transaction = payment.transaction;

      if (!transaction) {
        return null;
      }

      const categoryName = locationRentTransactions.includes(
        transaction as (typeof locationRentTransactions)[number]
      )
        ? 'Location Rent'
        : eventPaymentExpenseCategories[transaction];

      if (!categoryName) {
        return null;
      }

      const transactionLabel = eventPaymentTransactionLabel[transaction];

      const row: EventPaymentExpenseRow = {
        categoryName,
        date: payment.date,
        source: 'event-payment' as const,
        id: `event-payment-${payment.id}`,
        employeeName: payment.paidBy?.name,
        amount: Number(payment.amount || 0),
        notes: payment.notes || transactionLabel,
        paymentMethodName: payment.paymentMethod?.name,
      };

      return row;
    })
    .filter((row): row is NonNullable<typeof row> => Boolean(row));

export const matchesExpenseSearch = (
  row: {
    notes?: string;
    amount?: number;
    createdBy?: string;
    categoryName?: string;
    employeeName?: string;
    paymentMethodName?: string;
  },
  searchQuery?: string
) => {
  const query = searchQuery?.trim().toLowerCase();

  if (!query) {
    return true;
  }

  return [
    row.categoryName,
    row.amount,
    row.paymentMethodName,
    row.notes,
    row.employeeName,
    row.createdBy,
  ].some((value) =>
    String(value ?? '')
      .toLowerCase()
      .includes(query)
  );
};
